import React, { useMemo } from "react";
import ReactDOMServer from "react-dom/server";

// region Languages
import useTranslation from "src/translations/useTranslation";
import { CanConsumptionMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Utils
import { sortByText, sortByValue } from "@utils/data-table-sorting";
import { NO_VALUE, RANKED_METRICS, formatHoursAndMinutes, formatMetricValue } from "../can-consumption-metrics";
import { CanConsumptionDashboardOrigin } from "../can-consumption.types";
// endregion Utils
// region Components
import NewDataTable, { DataTableColumns } from "@molecules/NewDataTable";
// endregion Components

interface PlantTotalsTableProps {
  origins: CanConsumptionDashboardOrigin[];
  decimalSeparator: "." | ",";
}

/** One row of the table, carrying the markup of the plant cell and the rank that keeps the bucket last. */
interface PlantRow {
  origin: CanConsumptionDashboardOrigin;
  /** `0` for a named plant, `1` for the bucket row — a fixed ordering the customer cannot undo */
  bucketRank: number;
  name: string;
  nameHtml: string;
}

/** A value with no basis of calculation is a dash, never a zero — and it is drawn as one. */
const valueHtml = (text: string): string => (
  text === NO_VALUE ? ReactDOMServer.renderToString(<span className="no-value">{text}</span>) : text
);

/**
 * Totals of the period broken down by plant.
 *
 * A travel belongs to the plant it loaded at; when it did not leave a plant, it belongs to the plant
 * its vehicle is allocated to. Only what neither criterion reaches — a vehicle with no allocated
 * plant registered — lands in the single bucket row with a null `idOrigin`. That row is labelled
 * here and never hidden, otherwise the plants would not add up to the fleet, and it is actionable:
 * registering the allocation of those vehicles empties it. It is not offered in the plant filter
 * either — only named plants are, and what it means is explained by the subtitle of the panel, not
 * inside the cell. Whatever column the customer orders by, it stays at the bottom: it is not a
 * plant, so it does not belong anywhere in the ranking of the plants.
 */
const PlantTotalsTable: React.FC<PlantTotalsTableProps> = ({ origins, decimalSeparator }) => {

  const { t } = useTranslation();

  const rows = useMemo<PlantRow[]>(() => origins.map((origin) => {
    // `t` is typed as possibly null across the platform, and the label of the bucket row is text.
    const name = origin.name ?? t(CanConsumptionMessages.plantsNoPlantRow) ?? "";

    return {
      origin,
      bucketRank: origin.idOrigin === null ? 1 : 0,
      name,
      nameHtml: ReactDOMServer.renderToString(<span className="vehicle-name">{name}</span>)
    };
  }), [origins, t]);

  /**
   * A numeric column: ordered by the number, drawn as the formatted text.
   * Without the raw number the DataTable would order the text and put "1.000" before "9".
   */
  const numberColumn = (
    title: string,
    pick: (origin: CanConsumptionDashboardOrigin) => number | null,
    format: (origin: CanConsumptionDashboardOrigin) => string
  ): DataTableColumns => ({
    title,
    data: sortByValue<PlantRow>((row) => pick(row.origin), (row) => valueHtml(format(row.origin))),
    className: "dt-center",
    filterable: false
  });

  const columns: DataTableColumns[] = [
    {
      title: t(CanConsumptionMessages.columnPlant),
      data: sortByText<PlantRow>((row) => row.name, (row) => row.nameHtml),
      filterable: false
    },
    numberColumn(t(CanConsumptionMessages.columnTravels), (o) => o.travelsCount, (o) => String(o.travelsCount)),
    numberColumn(
      t(CanConsumptionMessages.columnVolume),
      (o) => o.volumeM3,
      (o) => formatMetricValue(o.volumeM3, 1, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnDistance),
      (o) => o.distanceKm,
      (o) => formatMetricValue(o.distanceKm, 0, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnFuel),
      (o) => o.fuelLiters,
      (o) => formatMetricValue(o.fuelLiters, 0, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.tileFuelInside),
      (o) => o.fuelInsideTravelsLiters,
      (o) => formatMetricValue(o.fuelInsideTravelsLiters, 0, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.tileFuelBetween),
      (o) => o.fuelBetweenTravelsLiters,
      (o) => formatMetricValue(o.fuelBetweenTravelsLiters, 0, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnAvgKmPerTravel),
      (o) => o.avgKmPerTravel,
      (o) => formatMetricValue(o.avgKmPerTravel, 1, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnAvgTravelTime),
      (o) => o.avgTravelMinutes,
      (o) => formatHoursAndMinutes(o.avgTravelMinutes)
    ),
    // km/L is among them and keeps its inverted reading: the accessor hands over the raw number, so
    // the "higher is better" column orders by the same rule as any other.
    ...RANKED_METRICS.slice(0, 3).map((descriptor): DataTableColumns => ({
      title: `${t(descriptor.shortLabelKey)} (${t(descriptor.unitKey)})`,
      data: sortByValue<PlantRow>(
        (row) => row.origin[descriptor.key],
        (row) => valueHtml(formatMetricValue(row.origin[descriptor.key], descriptor.fractionDigits, decimalSeparator))
      ),
      className: "dt-center",
      filterable: false
    })),
    // Invisible: it exists only to hold the bucket row at the bottom of every ordering.
    {
      title: "",
      data: (row: PlantRow) => row.bucketRank,
      visible: false,
      searchable: false,
      filterable: false
    }
  ];

  const settings: DataTables.Settings = {
    order: [[0, "asc"]],
    orderFixed: { pre: [[columns.length - 1, "asc"]] }
  };

  return (
    <div className="panel-table">
      <NewDataTable
        data={rows}
        columns={columns}
        settings={settings}
        showPaging={false}
        showInfo={false}
        showSearching={false}
        scrollHeight=""
      />
    </div>
  );
};

export default PlantTotalsTable;
